import React, { useContext, useRef, useEffect, useLayoutEffect, useState, useImperativeHandle } from 'react';
import classnames from 'classnames';
import { ListContext } from 'components/List';
import ChevronRightIcon from '@nokia-csf-uxr/ccfk-assets/ChevronRightIcon';
import { getContext, useCombinedRefs } from 'common';
import useHover from 'common/useHover';
import { CONSTANTS } from 'common/ListKeyboardNav';
import KEY_CODES from 'common/KeyCodes';
import { getMenuMaxHeight } from 'common/MenuDimensionCalc';
import Menu, { MenuProps, MenuRef } from 'components/Menu';
import Styled from './styles';

const {
  LIST_ITEM_TABABLE_ITEM,
  LIST_ITEM_SELECTED,
  LIST_ITEM_CASCADING,
  LIST_SUBMENU,
  NEXT_MENU,
} = CONSTANTS;

const {
  ARROW_RIGHT,
  ENTER_KEY,
  SPACE_KEY,
  ESCAPE,
} = KEY_CODES;

const LIST_ITEM_NAME = 'listitem';

// cascading List placement
const positioningOptions = {
  placement: 'right-start',
  modifiers: [
    { name: 'offset', enabled: true, options: { offset: [-8, 2] } },
    { name: 'flip', enabled: true, options: { fallbackPlacements: ['left', 'top', 'bottom'], } },
  ],
};

// return true if any string in the arrayList includes token
const hasToken = (arrayList, token) => {
  for (let ind = 0; ind < arrayList.length; ind += 1) {
    if (arrayList[ind].toLowerCase().includes(token.toLowerCase())) {
      return true;
    }
  }
  return false;
};

const isSelectionKeyPressed = key => key && (key === ENTER_KEY || key === SPACE_KEY || key === ARROW_RIGHT);

const ListItemCascadingContext = React.createContext<any>({});

export interface ListItemCascadingProps extends React.ComponentPropsWithoutRef<'div'> {
  /** The ListItem(s). These will appear as the display for the list item with the cascading menu. Consider using
   * `ListItemText, ListItemIcon`.
  */
  listItemNode: React.ReactNode;
  /** The element to use as the cascading menu indicator (usually a right chevron). */
  cascadingIcon?: React.ReactNode;
  /** Focus this item on mount. */
  autoFocus?: boolean;
  /** Display the list item as disabled. */
  disabled?: boolean;
  /** Display the list item as selected. */
  selected?: boolean;
  /** If `true`, the cascading Menu is opened. */
  isOpen?: boolean;
  /** Function called when the corresponding cascading List should open. */
  onOpen?: () => void;
  /** Function called when the corresponding cascading List should close.  */
  onClose?: () => void;
  /**  The flyout List, consider using `CascadingList`. */
  children: React.ReactNode;
  /** Menu properties. See Menu component documentation for full details. */
  menuProps?: Partial<MenuProps>;
  /** @ignore */
  onClick?: React.MouseEventHandler;
  /** @ignore */
  onKeyDown?: React.KeyboardEventHandler;
  /** @ignore */
  style?: React.CSSProperties;
}

export type ListItemCascadingHandle = {
  positionMenu: () => void;
  cascadingItemRef: HTMLElement;
};

/** ListItemCascading is a ListItem that has a flyout cascading List (CascadingList).
 * All other props will be provided to the root element.
 */
const ListItemCascading = React.forwardRef<ListItemCascadingHandle, ListItemCascadingProps>((props: ListItemCascadingProps, ref) => getContext(({ RTL }) => {
  const {
    children,
    autoFocus,
    disabled,
    selected,
    listItemNode,
    cascadingIcon,
    style,
    isOpen,
    onClose,
    onOpen,
    onClick,
    onKeyDown,
    menuProps,
    ...otherProps
  } = props;
  const [listMaxHeight, setListMaxHeight] = useState(null);
  const context = useContext(ListContext);
  const referenceElement = useRef(null);
  const menuRef = useRef<MenuRef>(null);
  const [hoverRef, isHovered] = useHover();
  const combinedRef = useCombinedRefs(referenceElement, hoverRef, ref);

  useEffect(() => {
    autoFocus && !disabled && combinedRef && combinedRef.current.focus();
  }, []); // autoFocus on mount only

  useEffect(() => {
    if (isHovered) {
      onOpen && onOpen();
    }
  }, [isHovered]);

  useEffect(() => {
    setListMaxHeight(getMenuMaxHeight(referenceElement.current));
  }, [referenceElement.current]);

  useImperativeHandle(ref, () => ({
    cascadingItemRef: referenceElement.current,
    positionMenu: () => {
      menuRef.current && menuRef.current.positionMenu();
    }
  }));

  useLayoutEffect(() => {
    menuRef.current && menuRef.current.positionMenu();
  }, [menuProps, style, RTL]);

  // callback from Menu when click outside. Need to make sure this menu needs to close since
  // with cascading menus if one of the cascading menus is clicked this one should not close.
  const handleMenuCloseCallback = (event) => {
    // if clicked does not contain the string listitem, close
    if (!hasToken(event.target.classList, LIST_ITEM_NAME)) {
      onClose && onClose();
    }
  };

  const handleOnKeyDown = (event) => {
    // Enter or Space should open menu
    const { key } = event;
    if (isSelectionKeyPressed(key)) {
      event.stopPropagation();
      onOpen && onOpen();
    }
    // escape should close menu
    if (key === ESCAPE) {
      event.stopPropagation();
      onClose && onClose();
    }
    onKeyDown && onKeyDown(event);
  };

  const handleOnClick = (event) => {
    onClick && onClick(event);
  };

  const NEXT_MENU_CLASS_NAME = `${NEXT_MENU}-${context.uniqueListId}`;
  const classes = classnames({
    [`${LIST_ITEM_TABABLE_ITEM}-${context.uniqueListId}`]: !disabled,
    [`${LIST_ITEM_CASCADING}-${context.uniqueListId}`]: !disabled,
    [LIST_ITEM_CASCADING]: !disabled,
    [LIST_ITEM_SELECTED]: !disabled && selected,
  });

  const menuClassNames = classnames({
    [LIST_SUBMENU]: true,
    [NEXT_MENU_CLASS_NAME]: true,
  });

  const resolvedMenuProps = {
    className: menuClassNames,
    ref: menuRef,
    positioningOptions,
    triggerEl: referenceElement,
    isOpen,
    usePortal: true,
    ...menuProps,
    onClose: (event) => { handleMenuCloseCallback(event); },
  };

  return (
    <Styled.ListItemCascading
      className={classes}
      aria-disabled={disabled}
      aria-expanded={isOpen}
      data-nextmenu={NEXT_MENU_CLASS_NAME}
      ref={combinedRef}
      RTL={RTL}
      $theme={context.theme}
      style={style}
      tabIndex={-1}
      disabled={disabled}
      selected={selected}
      hoverEffect={context.hoverEffect}
      cascadingListOpen={isOpen}
      onKeyDown={handleOnKeyDown}
      onClick={handleOnClick}
      {...otherProps}
    >
      <ListItemCascadingContext.Provider value={{ disabled, hoverEffect: context.hoverEffect }}>
        {listItemNode}
      </ListItemCascadingContext.Provider>
      <Styled.CascadingIndicator RTL={RTL} $theme={context.theme} disabled={disabled}>
        {cascadingIcon}
      </Styled.CascadingIndicator>
      <Menu {...resolvedMenuProps} data-prevmenu={`${LIST_ITEM_CASCADING}-${context.uniqueListId}`}>
        <ListItemCascadingContext.Provider value={{ listMaxHeight }}>
          {children}
        </ListItemCascadingContext.Provider>
      </Menu>
    </Styled.ListItemCascading>
  );
}));

ListItemCascading.displayName = 'ListItemCascading';

ListItemCascading.defaultProps = {
  disabled: false,
  selected: false,
  autoFocus: false,
  cascadingIcon: <ChevronRightIcon ariaLabel={null} />,
  isOpen: false,
  onOpen: undefined,
  onClose: undefined,
  menuProps: {},
  onClick: undefined,
  onKeyDown: undefined,
  style: {}
};

export {
  ListItemCascading as default,
  ListItemCascadingContext,
};
